import { Box, ListItem, PopperProps, Radio } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import * as React from "react";
import ErrorHelper from "./ErrorWrapper";
import Flex, { FlexProps } from "./Flex";
import { inputLabel } from "./MaskInput";
import { useFieldLock } from "./ReadOnlyFields";
import { CHART_DIMENSIONS } from "./app/provider/patient/chart";
import { PRIMITIVE } from "./ds";

export type AsyncSelectOption = { [k: string]: string };
export type AsyncSelectValue = { value: string; label: string };

interface AsyncSelectProps<T extends AsyncSelectOption> {
  label?: string;
  options?: T[];
  loading?: boolean;
  value?: T | null;
  onChange: (newValue: T | null) => void;
  formatKeys?: { val: string; label: string };
  width?: number;
  onSearch?: (v: string) => void;
  flexProps?: FlexProps;
  required?: boolean;
  error?: string | boolean;
  handleBlur?: any;
  placeholder?: string;
  name?: string;
  enableFilterOption?: boolean;
  optionsWithCheckbox?: boolean;
  /**
   * The field cannot be edited. Greyed out on an editable form; inside a
   * `ReadOnlyFields` region the value is presented instead.
   */
  disabled?: boolean;
  /** Alias of `disabled`, for call sites that read better as "read-only". */
  readOnly?: boolean;
  renderCustomLabel?: (opt: AsyncSelectOption) => React.ReactNode;
  renderSelectedValue?: (opt: AsyncSelectOption) => React.ReactNode;
  disableSortOptions?: boolean;
  forcePopupIcon?: boolean;
  CustomPopperComponent?: React.ComponentType<PopperProps>;
  isMatchButtonHeight?: boolean;
  disablePortal?: boolean;
}

function AsyncSelect<T extends AsyncSelectOption>({
  label,
  options = [],
  loading = false,
  value = null,
  onChange,
  formatKeys = { val: "value", label: "label" },
  flexProps,
  onSearch,
  required,
  error,
  placeholder = "",
  handleBlur,
  name,
  enableFilterOption,
  disabled,
  readOnly,
  optionsWithCheckbox = false,
  renderCustomLabel,
  renderSelectedValue,
  disableSortOptions,
  forcePopupIcon = true,
  CustomPopperComponent,
  isMatchButtonHeight = false,
  disablePortal = false,
}: Readonly<AsyncSelectProps<T>>) {
  const [open, setOpen] = React.useState(false);
  const lock = useFieldLock(disabled || readOnly);

  const handleOpen = () => {
    setOpen(true);
  };

  const CustomHandleBlur = (e: React.FocusEvent<HTMLDivElement, Element>) => {
    onSearch?.("");
    handleBlur?.(e);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputChange = (_: any, newInputValue: string, reason: string) => {
    if (reason === "input") {
      onSearch?.(newInputValue);
    }
  };

  return (
    <form autoComplete="off">
      <Flex
        {...flexProps}
        flexDirection={flexProps?.flexDirection ?? "column"}
        sx={{ height: `${CHART_DIMENSIONS.controlHeight} !important` }}
      >
        {/* {label && (
        <Text mb={"10px"}>
          {label}
          {required && (
            <Text fontSize={"13px"} component={"span"} color={"error"}>
              {` *`}
            </Text>
          )}
        </Text>
      )} */}
        <ErrorHelper error={error}>
          <Autocomplete
            disablePortal={disablePortal}
            sx={
              isMatchButtonHeight
                ? {
                    "& .MuiOutlinedInput-root": {
                      height: CHART_DIMENSIONS.controlHeight,
                      minHeight: CHART_DIMENSIONS.controlHeight,
                      padding: "0 8px !important",
                      display: "flex",
                      alignItems: "center",
                    },

                    "& .MuiOutlinedInput-input": {
                      height: "100% !important",
                      padding: "0 !important",
                      display: "flex",
                      alignItems: "center",
                      boxSizing: "border-box",
                    },

                    "& .MuiInputBase-root": {
                      height: CHART_DIMENSIONS.controlHeight,
                    },

                    "& .MuiFormControl-root": {
                      height: CHART_DIMENSIONS.controlHeight,
                    },
                  }
                : undefined
            }
            autoHighlight={false}
            autoSelect={false}
            // A presented select has no popup to open, so the dropdown arrow
            // would be an affordance that does nothing — drop it.
            forcePopupIcon={lock.readOnly ? false : forcePopupIcon}
            disabled={lock.disabled}
            readOnly={lock.readOnly}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            disableClearable={false}
            fullWidth
            onBlur={CustomHandleBlur}
            PopperComponent={CustomPopperComponent}
            value={value}
            slotProps={{ popper: { sx: { zIndex: 99999 } } }}
            onInputChange={handleInputChange}
            onChange={(_, val) => {
              onSearch?.("");
              return onChange(val);
            }}
            renderOption={(props, option) => (
              <ListItem
                {...props}
                key={option.value}
                sx={{ fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px` }}
              >
                {optionsWithCheckbox && (
                  <Radio
                    checked={
                      value?.[formatKeys.val ?? "value"] ===
                      option?.[formatKeys.val ?? "value"]
                    }
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                  />
                )}
                {renderCustomLabel
                  ? renderCustomLabel(option)
                  : option[formatKeys.label]}
              </ListItem>
            )}
            filterOptions={enableFilterOption ? (opt) => opt : undefined}
            isOptionEqualToValue={(option, value) =>
              option?.[formatKeys.val] === value?.[formatKeys.val]
            }
            getOptionLabel={(option) => option?.[formatKeys.label]}
            options={
              (disableSortOptions
                ? options?.filter(
                    (a) => value?.[formatKeys.val ?? "value"] !== a?.value
                  )
                : options?.sort((a, b) =>
                    a?.[formatKeys.label ?? "label"]?.localeCompare(
                      b?.[formatKeys.label ?? "label"]
                    )
                  )
              )?.filter(
                (a) =>
                  value?.[formatKeys.val ?? "value"] !==
                  a?.[formatKeys.val ?? "value"]
              ) ?? []
            }
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={inputLabel(label, required)}
                sx={{ fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px` }}
                InputLabelProps={{ shrink: true }}
                name={name}
                error={!!error}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
                onBlur={handleBlur}
                placeholder={placeholder}
                InputProps={{
                  ...params.InputProps,
                  startAdornment:
                    renderSelectedValue && value ? (
                      <Box
                        sx={{
                          fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px`,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mr: 1,
                        }}
                      >
                        {renderSelectedValue(value)}
                      </Box>
                    ) : undefined,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </ErrorHelper>
      </Flex>
    </form>
  );
}

export default AsyncSelect;
